import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
  UploadTask,
} from "firebase/storage";
import { getFirebaseApp } from "../firebase/firebaseService";
export interface FileUploadState {
  progress: number;
  downloadURL: string | null;
  error: string | null;
  isUploading: boolean;
}

const initialState: FileUploadState = {
  progress: 0,
  downloadURL: null,
  error: null,
  isUploading: false,
};

const uploadTasksMap = new Map<string, UploadTask>();

const fileUploadSlice = createSlice({
  name: "fileUpload",
  initialState,
  reducers: {
    startUpload(state) {
      state.isUploading = true;
      state.progress = 0;
      state.error = null;
      state.downloadURL = null;
    },
    setProgress(state, action: PayloadAction<number>) {
      state.progress = action.payload;
      state.error = null;
      state.downloadURL = null;
    },
    setDownloadURL(state, action: PayloadAction<string>) {
      state.downloadURL = action.payload;
      state.progress = 100;
      state.isUploading = false;
    },
    setError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.progress = 0;
      state.downloadURL = null;
      state.isUploading = false;
    },
    cancelUpload(state, action: PayloadAction<string>) {
      const uploadTask = uploadTasksMap.get(action.payload);
      if (uploadTask) {
        uploadTask.cancel();
        uploadTasksMap.delete(action.payload);
      }
      state.isUploading = false;
      state.progress = 0;
      state.downloadURL = null;
      state.error = null;
    },
    resetUploadState(state) {
      state.progress = 0;
      state.downloadURL = null;
      state.error = null;
      state.isUploading = false;
    },
  },
});

export const {
  setProgress,
  setDownloadURL,
  setError,
  cancelUpload,
  startUpload,
  resetUploadState,
} = fileUploadSlice.actions;

export const uploadFileToFirebaseStorage =
  (file: File, filePath: string) => async (dispatch: (action: unknown) => unknown) => {
    const storage = getStorage(getFirebaseApp());
    const storageRef = ref(storage, filePath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTasksMap.set(filePath, uploadTask);
    dispatch(startUpload());

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        dispatch(setProgress(progress));
      },
      (error) => {
        uploadTasksMap.delete(filePath);
        dispatch(setError(error.message));
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        uploadTasksMap.delete(filePath);
        dispatch(setDownloadURL(url));
      },
    );
  };

export const cancelFileUpload = (filePath: string) => (dispatch: (action: unknown) => unknown) => {
  dispatch(cancelUpload(filePath));
};

export default fileUploadSlice.reducer;
